import React from "react";
import moment from "moment";
import { cn } from "../../lib/utils";
import PaginatedTable, {
  PaginatedTableColumn,
  SortConfig,
} from "../ui/paginated-table";
import { BillingTransaction } from "../../model_gen/billing";

interface BillingHistoryTableProps {
  transactions: BillingTransaction[];
  loading: boolean;
  onViewTransaction: (transaction: BillingTransaction) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalItems?: number;
  sortConfig?: SortConfig<BillingTransaction>;
  onSort?: (key: keyof BillingTransaction) => void;
  showPagination?: boolean;
}

const BillingHistoryTable: React.FC<BillingHistoryTableProps> = ({
  transactions,
  loading,
  onViewTransaction,
  emptyTitle = "No billing history",
  emptyDescription = "No invoices have been generated yet.",
  currentPage,
  onPageChange,
  pageSize,
  totalItems,
  sortConfig,
  onSort,
  showPagination = true,
}) => {
  const formatDate = (timestamp: number) => {
    const date = moment.unix(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    const date = moment.unix(timestamp);
    return date.format("h:mm A");
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Paid: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
      Granted:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
      Subscription:
        "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
      Pending:
        "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
      Failed: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
    };
    return (
      colors[status] ||
      "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"
    );
  };

  const columns: PaginatedTableColumn<BillingTransaction>[] = [
    {
      key: "number",
      header: "TRANSACTION",
      width: "w-[30%]",
      sortable: true,
      render: (txn) => {
        if (txn.type === "invoice") {
          return (
            <div className="text-sm font-gc-reg text-foreground">
              {txn.number || txn.id}
            </div>
          );
        } else {
          // For credits: show receipt number if it's a purchase, description if it's a grant
          const displayText = txn.receipt_number
            ? txn.receipt_number
            : txn.description || txn.id;
          return (
            <div className="text-sm font-gc-reg text-foreground">
              {displayText}
            </div>
          );
        }
      },
    },
    {
      key: "status",
      header: "STATUS",
      width: "w-[15%]",
      sortable: true,
      render: (txn) => (
        <span
          className={cn(
            "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium",
            getStatusColor(txn.status)
          )}>
          {txn.status}
        </span>
      ),
    },
    {
      key: "amount",
      header: "AMOUNT",
      width: "w-[15%]",
      sortable: true,
      render: (txn) => (
        <div className="text-sm font-gc-reg text-foreground">
          ${Math.abs(txn.amount).toFixed(2)}
        </div>
      ),
    },
    {
      key: "created",
      header: "CREATED",
      width: "w-[30%]",
      sortable: true,
      render: (txn) => (
        <div className="text-sm font-gc-reg text-foreground">
          {formatDate(txn.created)}, {formatTime(txn.created)}
        </div>
      ),
    },
    {
      key: "id",
      header: "",
      width: "w-[10%]",
      render: (txn) => {
        const hasViewableContent =
          txn.type === "invoice" || (txn.type === "credit" && txn.receipt_url);

        if (!hasViewableContent) {
          return null;
        }

        return (
          <div className="text-sm font-gc-reg text-primary hover:underline cursor-pointer">
            View
          </div>
        );
      },
    },
  ];

  return (
    <PaginatedTable<BillingTransaction>
      data={transactions}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      totalItems={totalItems}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      onRowClick={(txn) => {
        // Allow clicking for invoices or credits with receipts
        if (
          txn.type === "invoice" ||
          (txn.type === "credit" && txn.receipt_url)
        ) {
          onViewTransaction(txn);
        }
      }}
      showPagination={showPagination}
      loadingRows={5}
    />
  );
};

export default BillingHistoryTable;
